import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { useUI } from "@/context/UIContext";
import { usePermissions } from "@/lib/permissions";
import { orgService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { SkeletonList } from "@/components/common/Skeletons";
import { AddFacilityDialog } from "@/components/dialogs/AddFacilityDialog";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, MapPin, Car, Activity, Bell, Hexagon, Plus, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Facilities() {
  const navigate = useNavigate();
  const { isManager } = usePermissions();
  const { refetchFacilities } = useUI();
  const { data, loading, refetch } = useFetch(() => orgService.facilities(), []);
  const facilities = data || [];
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const handleSaved = () => { refetch(); refetchFacilities(); };

  const removeFacility = async () => {
    setDeleting(true);
    try {
      await orgService.deleteFacility(deleteTarget.id);
      toast.success("Facility deleted");
      setDeleteTarget(null);
      refetch();
      refetchFacilities();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not delete facility");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Facilities"
        subtitle="Yards, depots and operational sites across your organization."
        actions={isManager && (
          <Button variant="secondary" onClick={() => setAddOpen(true)} data-testid="add-facility"><Plus className="mr-1.5 h-4 w-4" /> Add Facility</Button>
        )}
      />

      {/* auto-fit + minmax instead of a fixed 1/2/3-column breakpoint grid:
          with few facilities the existing cards stretch to fill the row
          (no dead whitespace next to a lone card), and as more are added
          it naturally wraps to more columns without a hardcoded count. */}
      {loading ? <SkeletonList rows={3} /> : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-4">
          {facilities.map((f) => (
            <Card key={f.id} onClick={() => navigate(`/facilities/${f.id}`)} className="group cursor-pointer overflow-hidden border-border/70 bg-card/50 transition hover:border-border hover:bg-card" data-testid={`facility-${f.id}`}>
              <div className="flex items-start justify-between p-5 pb-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-border/60 bg-secondary/40 text-foreground"><Building2 className="h-5 w-5" /></div>
                  <div><div className="font-display text-lg font-medium">{f.name}</div><div className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" /> {f.address}</div></div>
                </div>
                <div className="flex items-center gap-1">
                  {isManager && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(f); }}
                      className="rounded-md p-1.5 text-muted-foreground opacity-0 transition hover:bg-red-500/10 hover:text-red-400 group-hover:opacity-100"
                      data-testid={`facility-delete-${f.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  <ChevronRight className="h-5 w-5 text-muted-foreground transition group-hover:translate-x-0.5" />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-px border-t border-border/60 bg-border/50">
                <Metric icon={Car} label="Vehicles" value={f.vehicles} />
                <Metric icon={Activity} label="Active" value={f.activeVehicles} tone="text-emerald-400" />
                <Metric icon={Bell} label="Alerts" value={f.alerts} tone={f.alerts > 0 ? "text-primary" : ""} />
                <Metric icon={Hexagon} label="Geofences" value={f.geofences} />
              </div>
            </Card>
          ))}
        </div>
      )}

      {isManager && <AddFacilityDialog open={addOpen} onOpenChange={setAddOpen} onSaved={handleSaved} />}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete facility?"
        description={`This will remove "${deleteTarget?.name}". Vehicles and geofences homed here become unassigned rather than being deleted.`}
        confirmLabel={deleting ? "Deleting…" : "Delete"}
        destructive
        onConfirm={removeFacility}
      />
    </div>
  );
}

function Metric({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-card/70 p-4 text-center">
      <div className="mb-1 flex justify-center text-muted-foreground"><Icon className="h-3.5 w-3.5" /></div>
      <div className={cn("font-display text-xl font-semibold tabular-nums", tone)}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
